/**
 * Native fetch wrapper with:
 *   - credentials: 'include' (refresh cookie is scoped to /api/v1/auth)
 *   - Authorization: Bearer {accessToken} when in memory
 *   - 401 refresh-and-retry, narrow: only on AUTH_TOKEN_EXPIRED / AUTH_TOKEN_INVALID,
 *     skipped for /auth/login, /auth/refresh, /auth/logout, and already-retried requests
 *   - single-flight refresh per tab; cross-tab serialisation via navigator.locks
 *   - throws ApiError with the server's error envelope (code/message/details/requestId),
 *     or NetworkError when the request never reached the API at all
 */
import { ErrorCode, type RefreshResponse } from '@kreart/shared';
import { authStore } from '@/features/auth/store';

export const API_BASE_URL = (import.meta.env['VITE_API_URL'] ?? '/api/v1') as string;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(
    status: number,
    envelope: { code?: string; message: string; details?: unknown; requestId?: string },
  ) {
    super(envelope.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = envelope.code ?? 'UNKNOWN';
    this.details = envelope.details;
    this.requestId = envelope.requestId;
  }
}

/**
 * The request never reached the API: the server is not running, the dev proxy
 * has nothing to forward to, DNS/TLS failed, or the browser is offline.
 *
 * Kept apart from {@link ApiError} on purpose — nothing answered, so there is
 * no status and no error envelope to reason about, and the user's next step is
 * completely different from a server-side failure.
 */
export class NetworkError extends Error {
  /** True when the browser itself reports no connection. */
  readonly offline: boolean;
  /** Where the request was going — the only concrete detail we have. */
  readonly url: string;

  constructor(url: string, cause: unknown) {
    super(`Could not reach ${url}`);
    this.name = 'NetworkError';
    this.url = url;
    this.offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    this.cause = cause;
  }
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuthRefresh?: boolean;
  _retried?: boolean;
}

const AUTH_SKIP_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout'];
const REFRESH_TRIGGER_CODES = new Set<string>([
  ErrorCode.AUTH_TOKEN_EXPIRED,
  ErrorCode.AUTH_TOKEN_INVALID,
]);

let refreshInFlight: Promise<boolean> | null = null;

function shouldSkipAuthRefresh(path: string): boolean {
  return AUTH_SKIP_PATHS.some((p) => path.startsWith(p));
}

async function performRefresh(): Promise<boolean> {
  const url = `${API_BASE_URL}/auth/refresh`;
  const run = async (): Promise<boolean> => {
    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      });
      // 204 means there was no refresh cookie to rotate: no session, same as a 401.
      if (!res.ok || res.status === 204) return false;
      const data = (await res.json()) as RefreshResponse;
      authStore.setTokenRotated(data.accessToken, data.accessTokenExpiresAt);
      return true;
    } catch {
      return false;
    }
  };

  if (typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks?.request) {
    return navigator.locks.request('kreart-auth-refresh', { mode: 'exclusive' }, run);
  }
  return run();
}

function sharedRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { body, skipAuthRefresh, _retried, headers, ...rest } = options;
  const init: RequestInit = {
    ...rest,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...headers,
    },
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const token = authStore.snapshot.accessToken;
  if (token && !shouldSkipAuthRefresh(path)) {
    init.headers = { ...init.headers, authorization: `Bearer ${token}` };
  }

  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  // fetch only rejects when the exchange never happened; every HTTP status,
  // including 5xx, resolves and is handled below.
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (cause) {
    throw new NetworkError(url, cause);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json') ? await res.json() : await res.text();

  if (res.ok) {
    return payload as T;
  }

  const envelope =
    typeof payload === 'object' && payload !== null
      ? (payload as { code?: string; message?: string; details?: unknown; requestId?: string })
      : { message: typeof payload === 'string' ? payload : 'Request failed' };
  const err = new ApiError(res.status, {
    code: envelope.code,
    message: envelope.message ?? `HTTP ${res.status}`,
    details: envelope.details,
    requestId: envelope.requestId,
  });

  const canRefresh =
    res.status === 401 &&
    !skipAuthRefresh &&
    !_retried &&
    !shouldSkipAuthRefresh(path) &&
    REFRESH_TRIGGER_CODES.has(err.code);

  if (canRefresh) {
    const refreshed = await sharedRefresh();
    if (refreshed) {
      return apiFetch<T>(path, { ...options, _retried: true });
    }
    authStore.setUnauthenticated();
  }

  throw err;
}

export const api = {
  get: <T>(path: string, opts?: ApiRequestOptions) => apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: ApiRequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: ApiRequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts?: ApiRequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: ApiRequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
};
